import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"

import { AnimatedList } from "../AnimatedList"
import { Box } from "../Box"
import { Popover } from "../Popover"
import { Alert, type FooterButtons } from "."
import { type Colors } from "./Alert"

export interface AlertConfig {
  id?: string
  title: string
  subtitle: ReactNode
  buttons?: FooterButtons
  onRetry?: () => void
  color?: Colors
}

interface AlertContext {
  showAlert: (config: AlertConfig) => void
  /** Useful to ignore click events when interacting with the alerts.
   *
   * E.g. if you don't want to close a dialog when dismissing an alert. */
  containerRef: RefObject<HTMLDivElement> | null
}

const AlertContext = createContext<AlertContext>({
  showAlert: () => {},
  containerRef: null,
})

interface AlertProviderProps {
  children: ReactNode
}

export function AlertProvider(props: AlertProviderProps) {
  const { children } = props
  const [alertConfigs, setAlertConfigs] = useState<AlertConfig[]>([])

  const containerRef = useRef<HTMLDivElement>(null)

  const value = useMemo(
    () => ({
      showAlert: (config: AlertConfig) =>
        setAlertConfigs((prev) => [...prev, config]),
      containerRef,
    }),
    [],
  )

  function removeAlert(id?: string) {
    const newAlertConfigs = alertConfigs.filter((config) => config.id !== id)
    setAlertConfigs(newAlertConfigs)
  }

  return (
    <>
      <Popover
        open={alertConfigs.length > 0}
        align="end"
        anchor={<Box position="fixed" top={16} right={16} />}
        focusOnEnter={false}
      >
        <Box ref={containerRef} width={alertWidth}>
          <AnimatedList
            ariaLabel="Alert Container"
            items={alertConfigs}
            getItemKey={(item) => item.id ?? ""}
            stacked={{
              offset: 8,
              scaleFactor: 0.05,
              maxDepth: 4,
            }}
          >
            {(alertConfig) => {
              return (
                <Box width={alertWidth}>
                  <Alert
                    icon="alert"
                    title={alertConfig.title}
                    subtitle={alertConfig.subtitle}
                    rightButton={{
                      type: "close",
                      onClick: () => removeAlert(alertConfig.id),
                    }}
                    color={alertConfig.color ?? "error"}
                    footerButtons={getFooterButtons({
                      alertConfig,
                      onClose: () => removeAlert(alertConfig.id),
                    })}
                  />
                </Box>
              )
            }}
          </AnimatedList>
        </Box>
      </Popover>
      <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
    </>
  )
}

export function useAlert() {
  const context = useContext(AlertContext)

  if (context === undefined) {
    throw new Error("useAlert must be used within a AlertProvider")
  }

  return context
}

interface GetFooterButtonParams {
  alertConfig: AlertConfig
  onClose: () => void
}

function getFooterButtons(props: GetFooterButtonParams) {
  const { alertConfig, onClose } = props
  const { onRetry, buttons } = alertConfig

  function onContactSupport() {
    open("https://prismic.io/docs/help-center")
  }

  if (buttons) return buttons

  // Default buttons
  return {
    dismiss: {
      text: "Contact support",
      onClick: () => {
        onContactSupport()
        onClose()
      },
    },
    action: onRetry && {
      text: "Retry",
      onClick: () => {
        onClose()
        onRetry()
      },
    },
  }
}

const alertWidth = 600
